import { useTheme } from "styled-components";
import Modal from "common/Modal/Modal";
import React, { useContext, useState } from "react";
import { useWindowSize } from "react-use";
import { SummaryData } from "@shared/features/bots/BotForm/common/types";
import OrdersSummary from "features/bots/BotForm/Summaries/OrdersSummary";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType, LanguageNameSpaces } from "@shared/types";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import { HedgingMode } from "@shared/features/bots/BotForm/common/types";
import HedgingGridsSummary from "features/bots/BotForm/Summaries/HedgingGridsSummary";
import NewLookGridsSummary from "features/bots/BotForm/Summaries/NewLookGridsSummary";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface OrdersSummaryModalProps {
  setGridsSummaryModalOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  entriesSummaryData: SummaryData;
  takeProfitsSummaryData: SummaryData;
}

const OrdersSummaryModal = ({
  isOpen,
  setGridsSummaryModalOpen,
  entriesSummaryData,
  takeProfitsSummaryData,
}: OrdersSummaryModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { botType, hedgingMode } = useContext(BotFormContext)!;
  const { width } = useWindowSize();
  const { isNewLook } = useTheme();
  const [entriesErrorHeight, setEntriesErrorHeight] = useState(0);
  const [takeProfitsErrorHeight, setTakeProfitsErrorHeight] = useState(0);
  const isGridBot = botType === BotType.GRID;
  const isRangeHedgingBot =
    botType === BotType.HEDGING && hedgingMode === HedgingMode.RANGE;

  const getSummaries = () => {
    if (isRangeHedgingBot)
      return <HedgingGridsSummary orders={entriesSummaryData.orders} />;
    if (isGridBot && isNewLook) {
      // React wants the key passed directly, not spread in with the props.
      const { key, ...gridsSummaryData } = entriesSummaryData;
      return <NewLookGridsSummary key={key} {...gridsSummaryData} />;
    }
    const errorsHeight = Math.max(entriesErrorHeight, takeProfitsErrorHeight);
    const isSideBySide = !isGridBot;
    const entriesSummary = (
      <OrdersSummary
        {...entriesSummaryData}
        key={entriesSummaryData.key}
        setErrorHeight={setEntriesErrorHeight}
        errorsHeight={errorsHeight}
        isSideBySide={isSideBySide}
        sorterHeight={
          isSideBySide ? entriesSummaryData.sorterHeight : undefined
        }
      />
    );
    if (isGridBot) return entriesSummary;
    return (
      <EqualColumnsGrid numColumns={2} columnGap={4}>
        {entriesSummary}
        <OrdersSummary
          {...takeProfitsSummaryData}
          key={takeProfitsSummaryData.key}
          setErrorHeight={setTakeProfitsErrorHeight}
          errorsHeight={errorsHeight}
          isSideBySide={isSideBySide}
          sorterHeight={
            isSideBySide ? takeProfitsSummaryData.sorterHeight : undefined
          }
        />
      </EqualColumnsGrid>
    );
  };

  const baseWidth =
    isGridBot && isNewLook ? 680 : isGridBot || isRangeHedgingBot ? 600 : 860;

  return (
    <Modal
      onCancel={() => setGridsSummaryModalOpen(false)}
      open={isOpen}
      title={
        isRangeHedgingBot
          ? t("summary.hedging_summary")
          : isGridBot
          ? t("summary.grids_summary")
          : t("summary.orders_summary")
      }
      styles={{
        body: {
          minHeight: isNewLook ? undefined : 400,
          maxHeight: isNewLook ? "min(600px, 70vh)" : 600,
          overflow: "auto",
        },
      }}
      width={Math.min(baseWidth, width * 0.9)}
      footer={null}
      destroyOnHidden={true}
    >
      {getSummaries()}
    </Modal>
  );
};

export default OrdersSummaryModal;
